import React, { useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Modal } from '../ui'
import type { WatchlistItem, WatchlistList } from '../../services/watchlist'

interface WatchlistRemoveModalProps {
  open: boolean
  onClose: () => void
  item: WatchlistItem
  lists: WatchlistList[]
  displayLabel: string
  onRemoveFromLists: (listIds: number[]) => Promise<void>
  onRemoveAll: () => Promise<void>
}

/**
 * Mark lists for removal (trash toggles strikethrough); changes apply on confirm.
 */
const WatchlistRemoveModal: React.FC<WatchlistRemoveModalProps> = ({
  open,
  onClose,
  item,
  lists,
  displayLabel,
  onRemoveFromLists,
  onRemoveAll,
}) => {
  const [busy, setBusy] = useState(false)
  const [pendingRemove, setPendingRemove] = useState<Set<number>>(new Set())

  const onlyUncategorized = item.listIds.length === 0
  const allMarked =
    item.listIds.length > 0 &&
    item.listIds.every((id) => pendingRemove.has(id))
  const hasChanges = onlyUncategorized || pendingRemove.size > 0

  useEffect(() => {
    if (!open) return
    setPendingRemove(new Set())
  }, [open, item.id])

  function toggleList(listId: number) {
    setPendingRemove((prev) => {
      const next = new Set(prev)
      if (next.has(listId)) next.delete(listId)
      else next.add(listId)
      return next
    })
  }

  function toggleRemoveAll() {
    if (allMarked) {
      setPendingRemove(new Set())
    } else {
      setPendingRemove(new Set(item.listIds))
    }
  }

  async function handleConfirm() {
    if (!hasChanges) return
    setBusy(true)
    try {
      if (onlyUncategorized || allMarked) {
        await onRemoveAll()
      } else {
        await onRemoveFromLists([...pendingRemove])
      }
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fjern fra watchlist"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-ink-500 hover:text-ink px-3 py-1.5"
          >
            Annuller
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !hasChanges}
            className="text-sm font-semibold rounded-md bg-signal hover:bg-signal-700 disabled:bg-ink-300 text-paper px-3 py-1.5 transition-colors"
          >
            Bekræft ændringer
          </button>
        </>
      }
    >
      <p className="mb-3 text-ink-500">{displayLabel}</p>

      {onlyUncategorized ? (
        <p className="text-ink">Fjernes helt fra watchlist.</p>
      ) : (
        <div className="space-y-3">
          <ul className="space-y-1">
            {item.listIds.map((listId) => {
              const name =
                lists.find((l) => l.id === listId)?.name ?? `Liste ${listId}`
              const marked = pendingRemove.has(listId)
              return (
                <li
                  key={listId}
                  className={`flex items-center justify-between gap-2 rounded-sm px-1 py-1 ${
                    marked ? 'opacity-80' : ''
                  }`}
                >
                  <span
                    className={`text-sm min-w-0 flex-1 truncate ${
                      marked
                        ? 'line-through text-ink-500'
                        : 'text-ink'
                    }`}
                    title={name}
                  >
                    {name}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleList(listId)}
                    aria-label={
                      marked
                        ? `Fortryd fjernelse fra ${name}`
                        : `Markér ${name} til fjernelse`
                    }
                    aria-pressed={marked}
                    className={`shrink-0 p-1.5 rounded-sm transition-colors ${
                      marked
                        ? 'text-ink-500 hover:text-ink bg-paper-200'
                        : 'text-ink-500 hover:text-signal hover:bg-signal-50'
                    }`}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </li>
              )
            })}
          </ul>

          <button
            type="button"
            onClick={toggleRemoveAll}
            className={`text-[13px] font-semibold transition-colors ${
              allMarked
                ? 'text-ink font-medium'
                : 'text-signal hover:text-signal-700'
            }`}
          >
            {allMarked ? 'Fortryd fjern fra alle lister' : 'Fjern fra alle lister'}
          </button>
        </div>
      )}
    </Modal>
  )
}

export default WatchlistRemoveModal
